import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';

// Wire the Android hardware/gesture Back button to in-app navigation. Without
// this, Capacitor's default handling can exit the app (or do nothing) when the
// WebView has no history behind the current route — e.g. when the app loads
// directly on /inbox/:chatId after the tenant-rebind reload. No-op on web.
//
// Behaviour:
//   /inbox/:chatId  -> back to the conversation list (/inbox)
//   any other screen -> navigate back one step
//   /inbox (list root) -> exit the app
export function useAndroidBack(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle: { remove: () => void } | undefined;
    let cancelled = false;

    void import('@capacitor/app').then(({ App }) => {
      if (cancelled) return;
      void App.addListener('backButton', () => {
        // Read the live path at press time so this listener registers once.
        const path = window.location.pathname;
        if (/^\/inbox\/.+/.test(path)) {
          navigate('/inbox');
        } else if (path !== '/inbox' && path !== '/') {
          navigate(-1);
        } else {
          void App.exitApp();
        }
      }).then((h) => {
        handle = h;
        if (cancelled) h.remove();
      });
    });

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [navigate]);
}

/** Mountable wrapper so the hook can live inside <BrowserRouter>. */
export function NativeBackHandler(): null {
  useAndroidBack();
  return null;
}
